import { Component, inject, Input, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule } from '@ngx-translate/core';
import { WebchatLearnMoreComponent } from '../webchat-learn-more/webchat-learn-more.component';
import { MatDialog } from '@angular/material/dialog';
import { catchError, firstValueFrom, throwError } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ConversationApiService } from '@vendasta/conversation';
import { Widget } from '../webchat-form/widget';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

const CREATE_WEBCHAT_URL = `../widgets/new`;
const WEBCHAT_ICON = 'https://vstatic-prod.apigateway.co/business-center-client/assets/webchat-icon.svg';

@Component({
  selector: 'inbox-webchat-config-card',
  standalone: true,
  templateUrl: './webchat-config-card.component.html',
  styleUrls: ['./webchat-config-card.component.scss'],
  imports: [MatCardModule, GalaxyBadgeModule, MatButtonModule, MatSlideToggleModule, TranslateModule, AsyncPipe, NgIf],
})
export class WebchatConfigCardComponent {
  @Input() set widget(value: Widget) {
    this.webchat.set(value);
  }
  @Input() created?: boolean = true;

  webchat = signal<Widget | null>(null);
  protected readonly webchatIcon = WEBCHAT_ICON;

  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private route = inject(ActivatedRoute);
  private readonly conversationApiService = inject(ConversationApiService);
  private snackbarService = inject(SnackbarService);

  protected async handleToggleChange(): Promise<void> {
    try {
      const resp = await firstValueFrom(
        this.conversationApiService
          .updateWidget({
            widgetId: this.webchat().widgetId,
            isEnabled: !this.webchat().isEnabled,
            fieldMask: {
              paths: ['is_enabled'],
            },
          })
          .pipe(catchError((err) => throwError(() => err))),
      );
      this.webchat.set(resp.widget);
      this.snackbarService.openSuccessSnack('INBOX.WEBCHAT.SETTINGS.WEBCHAT_UPDATED');
    } catch (e) {
      this.snackbarService.openErrorSnack('INBOX.WEBCHAT.SETTINGS.WEBCHAT_UPDATE_ERROR');
    }
  }

  protected learnMore(): void {
    this.dialog.open(WebchatLearnMoreComponent, {
      width: '660px',
    });
  }

  protected webChatCardRedirect(): void {
    this.router.navigate([this.created ? `../widgets/${this.webchat().widgetId}/edit` : CREATE_WEBCHAT_URL], {
      relativeTo: this.route,
    });
  }
}
